import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Collapse, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import IosShareIcon from "@mui/icons-material/IosShare";
import { isMobileTeamViewport } from "../utils/teamNavigation";

const DISMISS_KEY = "xpro_pwa_install_dismissed_v1";

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true
    );
  } catch {
    return false;
  }
}

function isIos() {
  const ua = String(window.navigator?.userAgent || "");
  return /iPad|iPhone|iPod/i.test(ua) || (window.navigator?.platform === "MacIntel" && window.navigator?.maxTouchPoints > 1);
}

/**
 * Banner leve para instalar o PWA (necessário no iOS para Web Push de ligações).
 */
export default function VoicePwaInstallBanner({ enabled = true }) {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const dismissed = useMemo(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || dismissed || isStandalone()) return undefined;
    if (!isMobileTeamViewport() && !isIos()) return undefined;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    if (isIos()) {
      setIosHint(true);
      setOpen(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, [enabled, dismissed]);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) {
      /* ignore */
    }
    setDeferred(null);
    dismiss();
  }, [deferred, dismiss]);

  if (!open) return null;

  return (
    <Collapse in={open}>
      <Alert
        severity="info"
        icon={iosHint && !deferred ? <IosShareIcon fontSize="inherit" /> : <GetAppIcon fontSize="inherit" />}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {deferred ? (
              <Button color="inherit" size="small" onClick={install} sx={{ textTransform: "none", fontWeight: 700 }}>
                Instalar
              </Button>
            ) : null}
            <IconButton aria-label="Fechar" color="inherit" size="small" onClick={dismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{ borderRadius: 0 }}
      >
        <Typography variant="body2" fontWeight={700}>
          Instale o app para receber ligações no celular
        </Typography>
        <Typography variant="caption" display="block">
          {iosHint && !deferred
            ? "No iPhone: toque em Compartilhar → Adicionar à Tela de Início. Depois permita notificações."
            : "Com o app instalado, as notificações de ligação funcionam melhor em segundo plano."}
        </Typography>
      </Alert>
    </Collapse>
  );
}
